import { api } from './client.js';

export type StorageProviderId = 'tencent_cos' | 'amazon_s3';

/**
 * The storage settings shown in the UI.
 *
 * `secretKey` is deliberately absent: the server only reports whether one is
 * stored, so a live secret never reaches the browser.
 */
export interface StorageSettingsView {
  provider: StorageProviderId;
  region: string;
  bucket: string;
  accessKey: string;
  endpoint: string | null;
  hasSecretKey: boolean;
  configured: boolean;
  /** Credentials came from env vars, so the UI cannot change them. */
  lockedByEnv: boolean;
  /** When false, saved credentials are lost on restart. */
  persist: boolean;
}

export interface StorageSettingsInput {
  provider: StorageProviderId;
  region: string;
  bucket: string;
  accessKey: string;
  /** Leave empty to keep the stored secret key unchanged. */
  secretKey: string;
  endpoint: string;
}

export interface SaveStorageResult {
  ok: true;
  storage: StorageSettingsView;
}

export interface TestStorageResult {
  ok: boolean;
  host?: string;
  message: string;
}

/**
 * Login-password state, used by onboarding to decide whether the user is
 * still on the environment-seeded password.
 */
export interface AccountView {
  /** False while the JNOTE_APP_PASSWORD seed is still in effect. */
  passwordCustomized: boolean;
  /** Minimum length enforced by POST /auth/password. */
  passwordMinLength: number;
  /**
   * True when the server was started with JNOTE_ALLOW_DATA_RESET=true —
   * the only case where the settings UI offers the destructive data reset.
   */
  dataResetEnabled: boolean;
}

export interface ResetDataResult {
  ok: true;
  /** True when a UI-saved password existed and was removed. */
  passwordReset: boolean;
  /** True when a UI-saved storage config existed and was removed. */
  storageReset: boolean;
}

export interface BackupRunResult {
  key: string;
  sizeBytes: number;
  /** Pruned snapshot keys, oldest first. */
  deleted: string[];
}

export const settingsApi = {
  /** Current storage settings. Requires a session. */
  getStorage: () => api.get<{ storage: StorageSettingsView }>('/settings/storage'),

  /** Login-password state for onboarding. Requires a session. */
  getAccount: () => api.get<{ account: AccountView }>('/settings/account'),

  /**
   * Wipes the login password and cloud storage credentials, drops every
   * session and returns this deployment to the first-run state. The caller
   * must clear its local flags and reload to land in the onboarding wizard.
   * 404 when the operator did not enable the flag.
   */
  resetData: () => api.post<ResetDataResult>('/settings/reset-data'),

  /**
   * Takes one database snapshot to cloud storage immediately. 400 when
   * storage is unconfigured; 501 when the server disabled backups via env.
   */
  runBackup: () => api.post<{ backup: BackupRunResult }>('/settings/backup/run', {}),

  /**
   * Saves credentials and applies them immediately — no restart needed.
   * A 409 means env vars own the credentials and the UI cannot override them.
   */
  saveStorage: (input: StorageSettingsInput) =>
    api.put<SaveStorageResult>(
      '/settings/storage',
      typeof input === 'string' ? input : JSON.stringify(input),
      'application/json; charset=utf-8',
    ),

  /**
   * Checks credentials against the bucket without persisting them.
   * With no candidate supplied, tests the stored config.
   */
  testStorage: (candidate?: StorageSettingsInput) =>
    api.post<TestStorageResult>('/settings/storage/test', candidate ?? {}),
};
